import { useRef } from 'react';
import { ChevronDown, Loader2, Phone, Plus, RotateCw } from 'lucide-react';
import type { StripModel } from '@/lib/dealCard';

interface Props {
  model: StripModel;
  onOpenStage: () => void;
  onOpenCard: () => void;
  onCall: () => void;
  onCreateLead: () => void;
  onRetry: () => void;
  creating: boolean;
  calling: boolean;
}

/** Потянуть полосу вниз больше чем на столько px — открыть карточку (прототип: 25). */
const PULL_PX = 25;

/**
 * Полоса сделки над перепиской (прототип, .strip): стадия цветной таблеткой ▾, сумма, «Карточка ▾»,
 * круглая кнопка звонка. Нажатие на «Карточка» или потяг полосы вниз открывает окно сделки.
 * Если сделки нет — «Сделки нет» и «Создать лид».
 */
export function DealStrip({ model, onOpenStage, onOpenCard, onCall, onCreateLead, onRetry, creating, calling }: Props) {
  const y0 = useRef<number | null>(null);
  const pull = {
    onPointerDown: (e: React.PointerEvent) => { y0.current = e.clientY; },
    onPointerUp: (e: React.PointerEvent) => {
      if (y0.current !== null && e.clientY - y0.current > PULL_PX && model.kind === 'deal') onOpenCard();
      y0.current = null;
    },
    onPointerCancel: () => { y0.current = null; },
  };

  const callBtn = (
    <button type="button" className="callbtn" aria-label="Позвонить" onClick={onCall} disabled={calling}>
      {calling ? <Loader2 className="spin-i" aria-hidden /> : <Phone aria-hidden />}
    </button>
  );

  return (
    <div className="strip" aria-label="Сделка" {...pull}>
      {model.kind === 'loading' && <span className="soon sk-line" role="status">Загружаем сделку…</span>}

      {model.kind === 'error' && (
        <>
          <span className="soon" role="alert">Сделка не загрузилась</span>
          <button type="button" className="sbtn plain" onClick={onRetry} aria-label="Повторить загрузку сделки"><RotateCw aria-hidden />Повторить</button>
        </>
      )}

      {model.kind === 'other_project' && <span className="soon" role="status" style={{ marginLeft: 0 }}>{model.message}</span>}

      {model.kind === 'none' && (
        <>
          <span className="nodeal">Сделки нет</span>
          <button type="button" className="sbtn mk" onClick={onCreateLead} disabled={creating}>
            {creating ? <Loader2 className="spin-i" aria-hidden /> : <Plus aria-hidden />}Создать лид
          </button>
          <span className="grow" />
          {callBtn}
        </>
      )}

      {model.kind === 'deal' && (
        <>
          <button
            type="button"
            className="sbtn"
            style={{ background: `${model.stage.color}22`, color: model.stage.color }}
            onClick={onOpenStage}
            aria-label={`Стадия: ${model.stage.name}. Изменить`}
          >
            <span className="sdot" style={{ background: model.stage.color }} />
            {model.stage.name}
            <ChevronDown aria-hidden />
          </button>
          <span className="amt tnum">{model.amountText}</span>
          <button type="button" className="cardbtn" onClick={onOpenCard}>Карточка<ChevronDown aria-hidden /></button>
          {callBtn}
        </>
      )}
    </div>
  );
}
